import React from "react";
import SiteHeader from "../components/SiteHeader";
import { EmployerHero } from "../components/employer/EmployerHero";
import { IntakePackageCard } from "../components/employer/IntakePackageCard";
import { EmployerFooterCTA } from "../components/employer/EmployerFooterCTA";
import { Heart, FileText, QrCode } from "lucide-react";

/**
 * Public Employer Experience landing page.
 * Route: /employers
 * Nav label: "For Employers" (copy independent of URL — approved).
 */
export default function EmployersPage() {
  const valueBlocks = [
    {
      icon: Heart,
      title: "You see the person, not just the paper.",
      body: "Every applicant arrives with a short intro video, a resume, a cover letter, and a signed success agreement — all linked from one printable QR code.",
    },
    {
      icon: FileText,
      title: "Applicant-first, employer-friendly.",
      body: "We help candidates present their best selves. You get a clearer signal than a resume alone can carry.",
    },
    {
      icon: QrCode,
      title: "No dashboards to learn. No software to install.",
      body: "The Employer Intake Package is a professional service. We prepare it for you. You focus on hiring.",
    },
  ];

  const steps = [
    {
      n: "1",
      title: "Tell us what you're hiring for",
      body: "A short form covers your role, timeline, and priorities.",
    },
    {
      n: "2",
      title: "We prepare your package",
      body: "Inter-View reviews your needs and prepares tailored applicant onboarding materials.",
    },
    {
      n: "3",
      title: "You receive everything ready to use",
      body: "Delivered via email with clear instructions.",
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="employers-page">
      <SiteHeader />
      <EmployerHero />

      {/* Value blocks */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {valueBlocks.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-lg p-6 lg:p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  data-testid={`employer-value-block-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 leading-snug">
                    {v.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Intake Package Card */}
      <IntakePackageCard />

      {/* How it works */}
      <section
        id="how-it-works"
        className="py-16 lg:py-20 bg-gray-50"
        data-testid="employer-how-it-works"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How the Employer Intake Package works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((s, i) => (
              <div
                key={i}
                className="text-center"
                data-testid={`employer-step-${i + 1}`}
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-blue-600 text-white text-2xl font-bold flex items-center justify-center mb-4 shadow-md">
                  {s.n}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {s.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <EmployerFooterCTA />
    </div>
  );
}
